import React, { useState, useMemo } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  useToast,
  InputGroup,
  InputRightElement,
  IconButton,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  SimpleGrid,
  Icon,
} from '@chakra-ui/react'
import { FiEye, FiEyeOff, FiCheck, FiX, FiLock } from 'react-icons/fi'
import apiService from '@/services/api'
import { useAppTranslation } from '@/contexts/LanguageContext'

interface PasswordChangeData {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

interface PasswordErrors {
  currentPassword?: string
  newPassword?: string
  confirmPassword?: string
  general?: string
}

export default function SecurityContent() {
  const toast = useToast()
  const [isLoading, setIsLoading] = useState(false)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const { t } = useAppTranslation('common')

  const [passwordData, setPasswordData] = useState<PasswordChangeData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  })

  const [errors, setErrors] = useState<PasswordErrors>({})

  const strengthChecks = useMemo(() => {
    const pw = passwordData.newPassword
    return {
      minLength: pw.length >= 12,
      specialSymbol: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(pw),
      oneNumber: /\d/.test(pw),
    }
  }, [passwordData.newPassword])

  const isFormValid = useMemo(() => {
    return (
      passwordData.currentPassword.length > 0 &&
      strengthChecks.minLength &&
      strengthChecks.specialSymbol &&
      strengthChecks.oneNumber &&
      passwordData.confirmPassword.length > 0 &&
      passwordData.newPassword === passwordData.confirmPassword &&
      passwordData.currentPassword !== passwordData.newPassword
    )
  }, [passwordData, strengthChecks])

  const validatePasswordForm = (): boolean => {
    const newErrors: PasswordErrors = {}

    if (!passwordData.currentPassword) {
      newErrors.currentPassword = t('profile.security.validation.currentPasswordRequired')
    }

    if (!passwordData.newPassword) {
      newErrors.newPassword = t('profile.security.validation.newPasswordRequired')
    } else if (passwordData.newPassword.length < 12) {
      newErrors.newPassword = t('profile.security.validation.passwordMinLength')
    }

    if (!passwordData.confirmPassword) {
      newErrors.confirmPassword = t('profile.security.validation.confirmPasswordRequired')
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = t('profile.security.validation.passwordsDoNotMatch')
    }

    if (passwordData.currentPassword === passwordData.newPassword && passwordData.newPassword) {
      newErrors.newPassword = t('profile.security.validation.passwordMustBeDifferent')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handlePasswordChange = async () => {
    if (!validatePasswordForm()) return

    setIsLoading(true)
    setErrors({})

    try {
      await apiService.updatePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      })

      toast({
        title: t('profile.security.passwordUpdated'),
        description: t('profile.security.passwordUpdatedDescription'),
        status: 'success',
        duration: 5000,
        isClosable: true,
      })

      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })
    } catch (error: any) {
      console.error('Password update failed:', error)
      const errorMessage =
        error.response?.data?.message || t('profile.security.validation.updateFailed')

      if (error.response?.status === 400 && errorMessage.includes('current password')) {
        setErrors({ currentPassword: t('profile.security.validation.currentPasswordIncorrect') })
      } else {
        setErrors({ general: errorMessage })
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (field: keyof PasswordChangeData, value: string) => {
    setPasswordData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

  const handleCancel = () => {
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })
    setErrors({})
  }

  return (
    <Box
      bg="white"
      borderRadius="xl"
      border="1px solid"
      borderColor="gray.200"
      borderLeft="3px solid"
      borderLeftColor="brand.400"
      shadow="sm"
      overflow="hidden"
    >
      {/* Card header */}
      <HStack
        justify="space-between"
        px={6}
        py={4}
        bg="brand.50"
        borderBottom="1px solid"
        borderColor="gray.100"
      >
        <HStack spacing={2}>
          <Icon as={FiLock} color="brand.500" boxSize={4} />
          <Text fontSize="md" fontWeight="semibold" color="gray.800">
            {t('profile.security.title')}
          </Text>
        </HStack>
      </HStack>

      {/* Card body */}
      <Box px={6} py={6}>
        <VStack spacing={5} align="stretch">
          {errors.general && (
            <Alert status="error" borderRadius="lg">
              <AlertIcon />
              <AlertTitle>{t('messages.error')}</AlertTitle>
              <AlertDescription>{errors.general}</AlertDescription>
            </Alert>
          )}

          {/* Current Password */}
          <FormControl isInvalid={!!errors.currentPassword}>
            <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600" textTransform="uppercase" letterSpacing="wide">
              {t('profile.security.currentPassword')}
            </FormLabel>
            <InputGroup>
              <Input
                type={showCurrentPassword ? 'text' : 'password'}
                value={passwordData.currentPassword}
                onChange={(e) => handleInputChange('currentPassword', e.target.value)}
                placeholder={t('profile.security.currentPasswordPlaceholder')}
                borderRadius="xl"
                bg="gray.50"
                border="1px solid"
                borderColor="gray.200"
                _focus={{
                  borderColor: 'brand.500',
                  boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
                  bg: 'white',
                }}
                _placeholder={{ color: 'gray.400' }}
                size="md"
              />
              <InputRightElement h="full">
                <IconButton
                  aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
                  icon={showCurrentPassword ? <FiEyeOff /> : <FiEye />}
                  variant="ghost"
                  size="sm"
                  color="gray.400"
                  _hover={{ color: 'gray.600' }}
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                />
              </InputRightElement>
            </InputGroup>
            <FormErrorMessage>{errors.currentPassword}</FormErrorMessage>
          </FormControl>

          {/* New Password + Confirm side by side */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl isInvalid={!!errors.newPassword}>
              <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600" textTransform="uppercase" letterSpacing="wide">
                {t('profile.security.newPassword')}
              </FormLabel>
              <InputGroup>
                <Input
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordData.newPassword}
                  onChange={(e) => handleInputChange('newPassword', e.target.value)}
                  placeholder={t('profile.security.newPasswordPlaceholder')}
                  borderRadius="xl"
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.200"
                  _focus={{
                    borderColor: 'brand.500',
                    boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
                    bg: 'white',
                  }}
                  _placeholder={{ color: 'gray.400' }}
                  size="md"
                />
                <InputRightElement h="full">
                  <IconButton
                    aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                    icon={showNewPassword ? <FiEyeOff /> : <FiEye />}
                    variant="ghost"
                    size="sm"
                    color="gray.400"
                    _hover={{ color: 'gray.600' }}
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  />
                </InputRightElement>
              </InputGroup>
              <FormErrorMessage>{errors.newPassword}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.confirmPassword}>
              <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600" textTransform="uppercase" letterSpacing="wide">
                {t('profile.security.confirmNewPassword')}
              </FormLabel>
              <InputGroup>
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={passwordData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  placeholder={t('profile.security.confirmPasswordPlaceholder')}
                  borderRadius="xl"
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.200"
                  _focus={{
                    borderColor: 'brand.500',
                    boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
                    bg: 'white',
                  }}
                  _placeholder={{ color: 'gray.400' }}
                  size="md"
                />
                <InputRightElement h="full">
                  <IconButton
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    icon={showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                    variant="ghost"
                    size="sm"
                    color="gray.400"
                    _hover={{ color: 'gray.600' }}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  />
                </InputRightElement>
              </InputGroup>
              <FormErrorMessage>{errors.confirmPassword}</FormErrorMessage>
            </FormControl>
          </SimpleGrid>

          {/* Password strength indicators */}
          <HStack spacing={6}>
            <StrengthCheck met={strengthChecks.minLength} label={t('profile.security.minChars')} />
            <StrengthCheck met={strengthChecks.specialSymbol} label={t('profile.security.specialSymbol')} />
            <StrengthCheck met={strengthChecks.oneNumber} label={t('profile.security.oneNumber')} />
          </HStack>

          {/* Action buttons */}
          <HStack spacing={3} pt={2} justify="flex-end">
            <Button
              variant="ghost"
              onClick={handleCancel}
              isDisabled={isLoading}
              borderRadius="lg"
              color="gray.600"
            >
              {t('buttons.cancel')}
            </Button>
            <Button
              colorScheme="brand"
              onClick={handlePasswordChange}
              isLoading={isLoading}
              isDisabled={!isFormValid}
              loadingText={t('profile.security.updating')}
              borderRadius="lg"
              px={6}
            >
              {t('profile.security.updatePassword')}
            </Button>
          </HStack>
        </VStack>
      </Box>
    </Box>
  )
}

function StrengthCheck({ met, label }: { met: boolean; label: string }) {
  return (
    <HStack spacing={2} transition="all 0.2s">
      <Icon
        as={met ? FiCheck : FiX}
        boxSize={3.5}
        color={met ? 'green.500' : 'red.400'}
      />
      <Text fontSize="xs" fontWeight="medium" color={met ? 'green.600' : 'gray.500'}>
        {label}
      </Text>
    </HStack>
  )
}
